import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Stethoscope, LockKey } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { withCredentials: true });
      toast.success("Mot de passe réinitialisé. Vous pouvez vous connecter.");
      nav("/login");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Le lien est invalide ou a expiré.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F7FB]">
      <div className="hidden lg:flex flex-col justify-between p-16 bg-[#1E3A5F] text-white relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <Stethoscope size={22} weight="duotone" className="text-[#D45A3F]" />
          <span className="font-serif text-xl">Questiomed</span>
        </Link>
        <div className="relative z-10">
          <p className="font-serif text-4xl leading-tight italic">
            "Choisissez un mot de passe fort. C'est la première ligne de défense de vos données patient."
          </p>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at bottom right, #3A8FA5, transparent 60%)" }} />
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
              <Stethoscope size={22} weight="duotone" className="text-[#3A8FA5]" />
              <span className="font-serif text-xl">Questiomed</span>
            </Link>
            <div className="w-14 h-14 rounded-full bg-[#EEF3F8] flex items-center justify-center mb-4">
              <LockKey size={26} weight="duotone" className="text-[#3A8FA5]" />
            </div>
            <h1 className="font-serif text-4xl mb-2">Nouveau mot de passe</h1>
            <p className="text-[#5F758F]">Choisissez un mot de passe d'au moins 8 caractères.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Nouveau mot de passe</Label>
              <Input id="password" type="password" required autoFocus minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                data-testid="reset-password" />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Confirmer</Label>
              <Input id="confirm" type="password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 h-12 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                data-testid="reset-confirm" />
            </div>
          </div>

          {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}

          <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white" data-testid="reset-submit">
            {busy ? "Enregistrement…" : "Enregistrer le nouveau mot de passe"}
          </Button>

          <p className="text-sm text-center text-[#5F758F]">
            <Link to="/login" className="text-[#1E3A5F] underline underline-offset-4">Revenir à la connexion</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
